import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Media, MediaFolder, Picture, Program, Video } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { TFunction, useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import {
  Input,
  Label,
  Radio,
  Textarea,
  Checkbox,
} from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import Feedback from "../../UI/Forms/FormElements/Feedback";
import { Select } from "../../common/Select";
import { toHHMMSS, toSeconds } from "../../../services/utils";
import { DatePicker } from "../../common/DatePicker";
import Creatable from "react-select/creatable";
import { useStores } from "../../../hooks/useStores";
import i18n from "../../../i18n";
import { AsyncPaginate } from "react-select-async-paginate";
import { VIMEO_PROJECTS_LIMIT } from "../../../config/const";
import { ProgramStore } from "../../../stores/ProgramStore";
import { UserStore } from "../../../stores/UserStore";
import { observer } from "mobx-react";
import { useLoadVimeoVideoOptions } from "../../../hooks/vimeoFoldersAndVideos/useLoadVimeoVideoOptions";

interface IVimeoMediaFolderSelector {
  programStore: ProgramStore;
  userStore: UserStore;
  selectedFolder: {
    label: string;
    value: string;
  };
  customLabel?: string;
  t: TFunction;
  onChangeFolder: (folderId: MediaFolder["projectId"]) => void;
  setSelectedFolder: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    }>
  >;
}

export const VimeoMediaFolderSelector: React.FC<IVimeoMediaFolderSelector> = ({
  programStore,
  userStore,
  selectedFolder,
  customLabel,
  t,
  onChangeFolder,
  setSelectedFolder,
}) => {
  const loadVimeoMediaOptions = useCallback(
    async (search: string, loadedOptions: any, { page }: any) => {
      await programStore.fetchMediaFolders(page, VIMEO_PROJECTS_LIMIT, search);
      return {
        options: [
          ...programStore.mediaFolders.map((folder: MediaFolder) => {
            return {
              label: folder.name,
              value: folder.projectId,
            };
          }),
        ],
        hasMore: search
          ? false
          : Math.ceil(
              userStore.vimeoUser.numberOfFolders / VIMEO_PROJECTS_LIMIT
            ) > page,
        additional: {
          page: page + 1,
        },
      };
    },
    [programStore, userStore.vimeoUser.numberOfFolders]
  );

  useEffect(() => {
    programStore.setMediaFolders([]);
    programStore.fetchMediaFolders(1, VIMEO_PROJECTS_LIMIT);
  }, []);

  return (
    <Row mt="10px">
      <Col noGutter>
        <FormControl fullWidth>
          <Label htmlFor="folderId">
            {customLabel ||
              t("components.screenProgramDetail.videoForm.videoFolder")}
          </Label>
          <AsyncPaginate
            name={"folderId"}
            value={selectedFolder}
            loadOptions={loadVimeoMediaOptions}
            onChange={(option: any) => {
              setSelectedFolder(option || null);
              if (option?.value) {
                onChangeFolder(option.value);
              }
            }}
            additional={{
              page: 1,
            }}
            loadingMessage={() => t("common.loading")}
          />
        </FormControl>
      </Col>
    </Row>
  );
};

type Props = {
  video: Video | null;
  isSubmitting: boolean;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
  defaultSeason: number | null;
  defaultEpisodeNumber: number | null;
  medias: Media[];
  program?: Program;
  onSubmit: (values: Video) => void;
  onCancel: () => void;
  onChangeFolder: (folderId: MediaFolder["projectId"]) => void;
};

const VideoForm: FC<Props> = observer(
  ({
    video,
    isSubmitting,
    editable = true,
    isAdmin = false,
    validable,
    defaultSeason,
    defaultEpisodeNumber,
    medias,
    program,
    onSubmit,
    onCancel,
    onChangeFolder,
  }) => {
    const { categoryStore, programStore, userStore } = useStores();
    const { t } = useTranslation();
    const [selectedFolder, setSelectedFolder] = useState<{
      label: string;
      value: string;
    }>(
      {} as {
        label: string;
        value: string;
      }
    );
    const [coverImage, setCoverImage] = useState<any>();

    const formik = useFormik({
      initialValues: {
        id: video?.id || null,
        title: video?.title || "",
        description: video?.description || "",
        externalId: video?.externalId || "",
        pictures: video?.pictures || ({} as Picture),
        inAppUrl: video?.inAppUrl || "",
        highlighted:
          video?.highlighted === true ? "isHighlighted" : "isNotHighlighted",
        season:
          program?.typology === "Movie"
            ? 1
            : video?.season || defaultSeason || 1,
        episodeNumber:
          program?.typology === "Movie"
            ? 1
            : video?.episodeNumber || defaultEpisodeNumber || 1,
        duration: video?.duration || undefined,
        validFrom: video?.validFrom ? new Date(video?.validFrom) : undefined,
        validUntil: video?.validUntil ? new Date(video?.validUntil) : undefined,
        isVipOnly: video?.isVipOnly || false,
        tags: video?.tags || [],
        categories:
          (video?.categories &&
            video.categories.map((category: any) => category.category.id)) ||
          [],
        mediaType: video?.mediaType || "youtube",
        mediaUrl: video?.mediaUrl || "",
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        title: Yup.string().required(
          t("components.screenProgramDetail.videoForm.titleRequired")
        ),
        externalId: Yup.string().required(
          t("components.screenProgramDetail.videoForm.urlRequired")
        ),
        season: Yup.number().min(
          1,
          t("components.screenProgramDetail.videoForm.seasonRequired")
        ),
        episodeNumber: Yup.number().min(
          1,
          t("components.screenProgramDetail.videoForm.episodeNumberRequired")
        ),
        highlighted: Yup.string().required(
          t("components.screenProgramDetail.videoForm.visibleInAppRequired")
        ),
        validFrom: Yup.string().required(
          t("components.screenProgramDetail.videoForm.validFromRequired")
        ),
        validUntil: Yup.string().required(
          t("components.screenProgramDetail.videoForm.validUntilRequired")
        ),
      }),
    });

    const [mediaDuration, setMediaDuration] = useState(
      (formik.values.duration && toHHMMSS(formik.values.duration)) || "00:00:00"
    );

    useEffect(() => {
      // Update Multimedia fields when ExternalId changed
      if (formik.values.externalId && formik.values.mediaType === "vimeo") {
        const media = medias.find(
          (media) => media.externalId === formik.values.externalId
        );
        if (media) {
          formik.setFieldValue("pictures", media.pictures);
          formik.setFieldValue("duration", media.duration);
          if (formik.values.title.length === 0) {
            formik.setFieldValue("title", media.name);
          }

          if (formik.values.description?.length === 0) {
            formik.setFieldValue("description", media.description);
          }

          try {
            setCoverImage(
              media.pictures
                ? media.pictures.sizes
                  ? media.pictures.sizes[6].link
                  : media.pictures[6].link
                : ""
            );
          } catch (_) {}
        } else if (video) {
          try {
            setCoverImage(
              video.pictures
                ? video.pictures.sizes
                  ? video.pictures.sizes[6].link
                  : video.pictures[6].link
                : ""
            );
          } catch (_) {}
        }
      } else if (
        formik.values.externalId &&
        formik.values.mediaType === "youtube"
      ) {
        const videoId = formik.values.externalId;
        const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        const hqUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

        // Prova maxresdefault: YouTube restituisce un placeholder 120x90 se non esiste
        const img = new Image();
        img.onload = () => {
          const bestUrl = img.naturalWidth > 120 ? maxResUrl : hqUrl;
          const setPictures = (highResUrl: string) => {
            formik.setFieldValue("pictures", {
              height: img.naturalWidth > 120 ? 1080 : 480,
              width: img.naturalWidth > 120 ? 1920 : 640,
              link: highResUrl,
              link_with_play_button: "",
              sizes: [
                {
                  height: 1080,
                  width: 1920,
                  link: highResUrl,
                },
                {
                  height: 720,
                  width: 1280,
                  link: highResUrl,
                },
                {
                  height: 540,
                  width: 960,
                  link: highResUrl,
                },
                {
                  height: 360,
                  width: 640,
                  link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
                },
                {
                  height: 166,
                  width: 295,
                  link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
                },
                {
                  height: 150,
                  width: 200,
                  link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
                },
                {
                  height: 75,
                  width: 100,
                  link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
                },
              ]
                .reverse()
                .map((i) => ({ link: i.link, width: i.width, height: i.height })),
            } as Picture);
            setCoverImage(highResUrl);
          };
          setPictures(bestUrl);
        };
        img.onerror = () => {
          // Se fallisce del tutto, usa hqdefault
          formik.setFieldValue("pictures", {
            height: 480,
            width: 640,
            link: hqUrl,
            link_with_play_button: "",
            sizes: [
              { height: 1080, width: 1920, link: hqUrl },
              { height: 720, width: 1280, link: hqUrl },
              { height: 540, width: 960, link: hqUrl },
              { height: 360, width: 640, link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` },
              { height: 166, width: 295, link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` },
              { height: 150, width: 200, link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg` },
              { height: 75, width: 100, link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg` },
            ]
              .reverse()
              .map((i) => ({ link: i.link, width: i.width, height: i.height })),
          } as Picture);
          setCoverImage(hqUrl);
        };
        img.src = maxResUrl;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      medias,
      formik.values.externalId,
      formik.values.title,
      formik.values.description,
    ]);

    const refreshCoverImage = () => {
      const media = medias.find(
        (media) => media.externalId === formik.values.externalId
      );
      if (media) {
        formik.setFieldValue("pictures", media.pictures);

        try {
          setCoverImage(
            //@ts-ignore
            media.pictures.sizes[6].link || media.pictures[6].link
          );
        } catch (_) {}
      }
    };

    const tagsOptions = useMemo(() => {
      return video && video.tags
        ? video?.tags.map((tag, index) => ({
            label: tag,
            value: index,
          }))
        : [];
    }, [video]);

    const categoryOptions = useMemo(() => {
      return categoryStore.multimediaCategories.map((multimediaCategory) => ({
        label: multimediaCategory.labels[i18n.language],
        value: multimediaCategory.id,
      }));
    }, [categoryStore.multimediaCategories]);

    const { loadVimeoVideoOptions, getSelectedVideoId } =
      useLoadVimeoVideoOptions({ programStore, selectedFolder, medias });

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="mediaType">
                  {t("components.screenProgramDetail.videoForm.mediaType")}
                </Label>
                <Radio
                  id="mediaType1"
                  name="mediaType"
                  value={"vimeo"}
                  label={"Vimeo"}
                  onChange={formik.handleChange}
                  checked={formik.values.mediaType === "vimeo"}
                />
                <Radio
                  id="mediaType2"
                  name="mediaType"
                  value={"youtube"}
                  label={"YouTube"}
                  onChange={formik.handleChange}
                  checked={formik.values.mediaType === "youtube"}
                />

                {formik.errors.mediaType && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("mediaType").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>

          {formik.values.mediaType === "vimeo" && (
            <VimeoMediaFolderSelector
              programStore={programStore}
              userStore={userStore}
              t={t}
              onChangeFolder={onChangeFolder}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
            />
          )}

          {formik.values.mediaType === "vimeo" && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="externalId">
                    {t("components.screenProgramDetail.videoForm.url")}
                  </Label>
                  <AsyncPaginate
                    key={programStore.mediaListTotal}
                    id={"externalId"}
                    name={"externalId"}
                    value={getSelectedVideoId(formik.values.externalId)}
                    loadOptions={loadVimeoVideoOptions}
                    onChange={(option: any) => {
                      formik.setFieldValue("externalId", option?.value || null);
                    }}
                    additional={{
                      page: 1,
                    }}
                    loadingMessage={() => t("common.loading")}
                    isDisabled={!selectedFolder.value || medias.length === 0}
                  />
                  {programStore.isLoading && (
                    <Feedback
                      state={"warning"}
                      showState={true}
                      showErrorOnly={false}
                    >
                      {t("common.loading")}
                    </Feedback>
                  )}
                  {formik.errors.externalId && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("externalId").error}
                    </Feedback>
                  )}
                </FormControl>
              </Col>
            </Row>
          )}

          {formik.values.mediaType === "youtube" && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="title">
                    {t("components.screenProgramDetail.videoForm.mediaUrl")}
                  </Label>
                  <Input
                    id="mediaUrl"
                    name="mediaUrl"
                    placeholder={""}
                    value={formik.values.mediaUrl}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldValue(
                        "externalId",
                        Boolean(e.target?.value)
                          ? e.target.value.split("/").pop()
                          : null
                      );
                    }}
                    state={formik.errors.mediaUrl ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("mediaUrl").error}
                  />
                  {formik.errors.externalId && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("externalId").error}
                    </Feedback>
                  )}
                </FormControl>
              </Col>
            </Row>
          )}

          <Row mt="10px">
            {coverImage && (
              <Label htmlFor="posterUrl">
                {t("components.screenProgramDetail.videoForm.coverImage")}
              </Label>
            )}
            <img src={coverImage} alt={formik.values.title} />
          </Row>
          {coverImage && video && (
            <Row mt="10px">
              <Button
                type={"reset"}
                onClick={refreshCoverImage}
                marginRight="10px"
                color="light"
              >
                {t("common.refresh")}
              </Button>
            </Row>
          )}
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.screenProgramDetail.videoForm.title")}
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder={t(
                    "components.screenProgramDetail.videoForm.titlePlaceholder"
                  )}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  state={formik.errors.title ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("title").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="description">
                  {t("components.screenProgramDetail.videoForm.description")}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t(
                    "components.screenProgramDetail.videoForm.descriptionPlaceholder"
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  showState={true}
                />
              </FormControl>
            </Col>
          </Row>
          {program?.typology === "TVShow" && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="season">
                    {t("components.screenProgramDetail.videoForm.season")}
                  </Label>
                  <Input
                    type="number"
                    id="season"
                    name="season"
                    placeholder={t(
                      "components.screenProgramDetail.videoForm.seasonPlaceholder"
                    )}
                    value={formik.values.season}
                    onChange={formik.handleChange}
                    state={formik.errors.season ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("season").error}
                    disabled
                  />
                </FormControl>
              </Col>
            </Row>
          )}

          {formik.values.mediaType === "vimeo" && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="duration">
                    {t("components.screenProgramDetail.videoForm.duration")}
                  </Label>
                  <Input
                    id="duration"
                    name="duration"
                    placeholder={t(
                      "components.screenProgramDetail.videoForm.durationPlaceholder"
                    )}
                    value={
                      (formik.values.duration &&
                        toHHMMSS(formik.values.duration)) ||
                      "00:00:00"
                    }
                    onChange={formik.handleChange}
                    state={formik.errors.duration ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("duration").error}
                    disabled
                  />
                </FormControl>
              </Col>
            </Row>
          )}

          {formik.values.mediaType === "youtube" && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="duration">
                    {t("components.screenProgramDetail.videoForm.duration")}
                  </Label>
                  <Input
                    id="duration"
                    name="duration"
                    placeholder={t(
                      "components.screenProgramDetail.videoForm.durationPlaceholder"
                    )}
                    value={mediaDuration}
                    onChange={(e) => {
                      setMediaDuration(e.target.value);
                      if (String(e.target?.value).split(":").length === 3) {
                        formik.setFieldValue(
                          "duration",
                          toSeconds(e.target.value)
                        );
                      }
                    }}
                    state={formik.errors.duration ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("duration").error}
                  />
                </FormControl>
              </Col>
            </Row>
          )}

          {program?.typology === "TVShow" && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="episodeNumber">
                    {t(
                      "components.screenProgramDetail.videoForm.episodeNumber"
                    )}
                  </Label>
                  <Input
                    type="number"
                    id="episodeNumber"
                    name="episodeNumber"
                    placeholder={t(
                      "components.screenProgramDetail.videoForm.episodeNumberPlaceholder"
                    )}
                    value={formik.values.episodeNumber}
                    onChange={formik.handleChange}
                    state={formik.errors.episodeNumber ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("episodeNumber").error}
                    disabled
                  />
                </FormControl>
              </Col>
            </Row>
          )}
          {/* <Row mt="10px">
          <Col noGutter>
            <FormControl fullWidth>
              <Label htmlFor="posterUrl">
                {t("components.screenProgramDetail.videoForm.posterUrl")}
              </Label>
              <Input
                id="posterUrl"
                name="posterUrl"
                placeholder={t(
                  "components.screenProgramDetail.videoForm.posterUrlPlaceholder"
                )}
                value={formik.values.posterUrl}
                onChange={formik.handleChange}
                state={formik.errors.posterUrl ? "error" : "success"}
                showState={true}
                feedbackText={formik.getFieldMeta("posterUrl").error}
              />
            </FormControl>
          </Col>
        </Row> */}

          {/* <Row mt="10px">
          <Col noGutter>
            <FormControl fullWidth>
              <Label htmlFor="inAppUrl">
                {t("components.screenProgramDetail.videoForm.inAppUrl")}
              </Label>
              <Input
                id="inAppUrl"
                name="inAppUrl"
                placeholder={t(
                  "components.screenProgramDetail.videoForm.inAppUrlPlaceholder"
                )}
                value={formik.values.inAppUrl}
                onChange={formik.handleChange}
                state={formik.errors.inAppUrl ? "error" : "success"}
                showState={true}
                feedbackText={formik.getFieldMeta("inAppUrl").error}
              />
            </FormControl>
          </Col>
        </Row> */}

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="highlighted">
                  {t("components.screenProgramDetail.videoForm.highlighted")}
                </Label>
                <Radio
                  id="highlighted1"
                  name="highlighted"
                  value={"isHighlighted"}
                  label={t(
                    "components.screenProgramDetail.videoForm.isHighlighted"
                  )}
                  onChange={formik.handleChange}
                  checked={formik.values.highlighted === "isHighlighted"}
                />
                <Radio
                  id="highlighted2"
                  name="highlighted"
                  value={"isNotHighlighted"}
                  label={t(
                    "components.screenProgramDetail.videoForm.isNotHighlighted"
                  )}
                  onChange={formik.handleChange}
                  checked={formik.values.highlighted === "isNotHighlighted"}
                />

                {formik.errors.highlighted && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("highlighted").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validFrom">
                  {t("components.screenProgramDetail.videoForm.validFrom")}
                </Label>

                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenProgramDetail.videoForm.validFromPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validFrom || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validFrom", date || null);
                  }}
                />
                {formik.errors.validFrom && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validFrom").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validUntil">
                  {t("components.screenProgramDetail.videoForm.validUntil")}
                </Label>
                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenProgramDetail.videoForm.validUntilPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validUntil || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validUntil", date || null);
                  }}
                />
                {formik.errors.validUntil && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validUntil").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row paddingTop="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="isVipOnly">
                  {t("components.common.programForm.isVipOnly")}
                </Label>
                <Checkbox
                  id="isVipOnly"
                  name="isVipOnly"
                  value={formik.values.isVipOnly.toString()}
                  label={""}
                  onChange={formik.handleChange}
                  checked={formik.values.isVipOnly}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="tags">
                  {t("components.common.programForm.tags")}
                </Label>
                <Creatable
                  id="tags"
                  name="tags"
                  options={tagsOptions}
                  isMulti
                  placeholder={t(
                    "components.common.programForm.tagsPlaceholder"
                  )}
                  value={formik.values.tags?.map((tag, index) => ({
                    label: tag,
                    value: index,
                  }))}
                  onChange={(options: any) => {
                    const selectedOptions = options as any;
                    formik.setFieldValue(
                      "tags",
                      options
                        ? (
                            selectedOptions as {
                              label: string;
                              value: string;
                            }[]
                          ).map((option) => option.label)
                        : []
                    );
                  }}
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="categories">
                  {t("components.common.programForm.categories")}
                </Label>
                <Select
                  id="categories"
                  name="categories"
                  options={categoryOptions}
                  // @ts-ignore
                  isMulti
                  value={formik.values.categories?.map((categoryID) => ({
                    label: categoryStore.multimediaCategories.find(
                      (category) =>
                        category.id?.toString() === categoryID.toString()
                    )?.labels[i18n.language],
                    value: categoryID,
                  }))}
                  onChange={(options: any) => {
                    const selectedOptions = options as any;
                    formik.setFieldValue(
                      "categories",
                      options
                        ? (
                            selectedOptions as {
                              label: string;
                              value: string;
                            }[]
                          ).map((option) => option.value.toString())
                        : []
                    );
                  }}
                />
                {formik.errors.categories && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("categories").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
        </Container>

        {/* isLoading={isSubmitting} */}
        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>
      </form>
    );
  }
);

export default VideoForm;
